'use client'

import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from 'react'
import { cn } from 'cn'
import { useDitherRepulsion } from '@/hooks/use-dither-repulsion'
import { supportsWebGL } from '@/lib/dither'

// Three.js stays out of the eager bundle — React.lazy (unlike next/dynamic)
// doesn't preload the chunk, so the ~240KB only downloads and evaluates once
// this section actually enters the viewport on a WebGL-capable client.
const DeliveryArtThree = lazy(() =>
  import('./delivery-art-three').then((m) => ({ default: m.DeliveryArtThree })),
)

type ToneMap = { width: number; height: number; rows: string[] }
type Shape = { tone: ToneMap; className: string }

/**
 * Section wrapper that draws dither art (octopus, clouds) behind its
 * content. `shapes` picks which pieces of `delivery-art.json` this
 * instance draws; CSS positions them (`.delivery-shape--{name}`).
 */
export function DeliveryPartner({
  children,
  shapes: names,
  className,
}: {
  children: ReactNode
  shapes?: string[]
  className?: string
}) {
  const rootRef = useRef<HTMLElement>(null)
  const artRef = useRef<HTMLDivElement>(null)
  const [artActive, setArtActive] = useState(false)
  const [shapes, setShapes] = useState<Shape[]>([])
  // null during SSR / before hydration; false falls back to DOM span dots.
  const [webgl, setWebgl] = useState<boolean | null>(() =>
    typeof document === 'undefined' ? null : supportsWebGL(),
  )

  // With WebGL, the hook only creates the shape divs — CSS-positioned
  // measurement shells the Three renderer reads — so no span dots exist.
  // Without it, the DOM dots render and animate exactly as before.
  useDitherRepulsion(rootRef, artRef, { shapes, enabled: webgl === false })

  // Activate only on real scroll intent or when the section enters the
  // viewport — a generous rootMargin would fire during initial page load.
  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const activate = () => setArtActive(true)
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        activate()
        io.disconnect()
      }
    })
    io.observe(el)
    const onScroll = () => {
      if (window.scrollY > 200) {
        activate()
        window.removeEventListener('scroll', onScroll)
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      io.disconnect()
      window.removeEventListener('scroll', onScroll)
    }
  }, [])

  // The ~210KB tone-map data also stays out of the eager bundle.
  useEffect(() => {
    if (!artActive || shapes.length) return
    let dead = false
    import('@/content/delivery-art.json').then((m) => {
      if (dead) return
      setShapes(
        Object.entries(m.default)
          .filter(([name]) => !names || names.includes(name))
          .map(([name, tone]) => ({
            tone: tone as ToneMap,
            className: `delivery-shape delivery-shape--${name}`,
          })),
      )
    })
    return () => {
      dead = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artActive])

  return (
    <section ref={rootRef} className={cn('delivery-partner', className)}>
      <div ref={artRef} className="delivery-art" aria-hidden="true" />
      {artActive && webgl && (
        <Suspense fallback={null}>
          <DeliveryArtThree
            rootRef={rootRef}
            artRef={artRef}
            names={names}
            onUnavailable={() => setWebgl(false)}
          />
        </Suspense>
      )}
      <div className="delivery-heading">{children}</div>
    </section>
  )
}
